import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { WHATSAPP_URL } from "../lib/constants";
import { useT } from "../i18n";
import { LanguageToggle } from "./LanguageToggle";

export const Navbar = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);

  const NAV_LINKS = [
    { id: "inicio", label: t("nav.inicio") },
    { id: "quem-somos", label: t("nav.quemSomos") },
    { id: "servicos", label: t("nav.servicos") },
    { id: "testemunhos", label: t("nav.testemunhos") },
    { id: "contacto", label: t("nav.contacto") },
  ];

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 12);
      const delta = y - lastY;
      if (y < 80) {
        setHidden(false);
      } else if (delta > 6) {
        setHidden(true);
      } else if (delta < -6) {
        setHidden(false);
      }
      lastY = y;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const onMouseMove = (e) => {
      if (e.clientY < 80) setHidden(false);
    };
    window.addEventListener("mousemove", onMouseMove);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, []);

  const shouldHide = hidden && !open;

  const scrollTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      // Retry a few times to wait for Landing to mount
      let attempts = 0;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (attempts++ < 10) {
          setTimeout(tryScroll, 80);
        }
      };
      setTimeout(tryScroll, 80);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ease-out ${
        shouldHide ? "-translate-y-full" : "translate-y-0"
      } ${
        scrolled
          ? "bg-[#FEFEFE]/85 backdrop-blur-xl border-b border-[#2A322C]/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-5 md:px-6 flex items-center justify-between h-[88px] md:h-[104px]">
        <button
          onClick={() => scrollTo("inicio")}
          data-testid="navbar-logo"
          className="flex items-center gap-3 group py-2"
          aria-label="Yin Motion"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_movimento-saude-1/artifacts/0fhdtt2f_logo%20para%20site.png"
            alt="Yin Motion — Saúde & Bem Estar"
            className="h-[72px] md:h-[88px] w-auto object-contain"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              data-testid={`nav-link-${link.id}`}
              className={`text-sm font-medium transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-px after:w-0 after:bg-current hover:after:w-full after:transition-all ${
                scrolled
                  ? "text-[#2A322C]/80 hover:text-[#A3BD98]"
                  : "text-white/90 hover:text-white"
              }`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-7 xl:gap-8">
          <LanguageToggle scrolled={scrolled} />
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp-btn"
            className={`text-sm font-medium px-5 py-2.5 transition-colors ${
              scrolled
                ? "bg-[#EE8984] text-white hover:bg-[#CF8376]"
                : "bg-[#EE8984] text-[#2A322C] hover:bg-white"
            }`}
          >
            {t("nav.whatsapp")}
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-4">
          <LanguageToggle scrolled={scrolled || open} />
          <button
            onClick={() => setOpen(!open)}
            className={`p-2 transition-colors ${
              scrolled || open ? "text-[#2A322C]" : "text-white"
            }`}
            data-testid="navbar-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden bg-[#FEFEFE] border-t border-[#2A322C]/10"
          data-testid="mobile-menu"
        >
          <nav className="flex flex-col px-6 py-6 gap-5">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                data-testid={`mobile-nav-link-${link.id}`}
                className="text-left font-serif text-2xl text-[#2A322C]"
              >
                {link.label}
              </button>
            ))}
            <div className="flex flex-col gap-3 pt-4 border-t border-[#E4E9DE]">
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-btn"
                className="bg-[#A3BD98] text-white text-center py-3 text-sm font-medium"
              >
                {t("nav.whatsapp")}
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
